// src/components/layout/WebLayout.jsx

import React, { useState, useEffect } from 'react';
import {
  Menu as MenuIcon,
  X as XIcon,
  Leaf,
  Sun,
  Moon,
  Home,
  Users,
  Wallet,
  ShoppingBag,
  LandPlot,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from 'lib/auth';
import { UserNavigation } from './components/UserNavigation';

export const WebLayout = ({ children }) => {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  const navLinks = [
    { to: '/app', label: 'Dashboard', icon: <Home size={18} /> },
    { to: '/app/market', label: 'Open Market', icon: <ShoppingBag size={18} /> },
    { to: '/app/lands', label: 'Lands', icon: <LandPlot size={18} /> },
    { to: '/app/funding', label: 'Funding', icon: <Users size={18} /> },
    { to: '/app/sacco', label: 'SACCO', icon: <Wallet size={18} /> },
  ];

  // Apply theme
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('theme', theme);
  }, [theme]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors">
      {/* NAVBAR */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/80 dark:bg-gray-900/80 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* LEFT: Logo + Mobile Menu Button */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              className="p-2 rounded-lg md:hidden hover:bg-gray-200 dark:hover:bg-gray-800"
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon size={22} />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-green-200 dark:bg-green-700 rounded-xl flex items-center justify-center">
                <Leaf size={20} className="text-green-700 dark:text-green-200" />
              </div>
              <span className="text-xl font-bold hidden sm:block">Fundo</span>
            </Link>
          </div>

          {/* DESKTOP NAV */}
          <nav className="hidden md:flex items-center gap-6">
            {user &&
              navLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-sm"
                >
                  {item.icon}
                  {item.label}
                </Link>
              ))}

            {/* Toggle Theme */}
            <button
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* User Navigation */}
            {user && <UserNavigation />}
          </nav>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />

            <motion.aside
              className="fixed top-0 left-0 h-full w-72 bg-white dark:bg-gray-900 p-6 z-50 shadow-xl"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
            >
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-green-200 dark:bg-green-700 rounded-xl flex items-center justify-center">
                    <Leaf size={20} />
                  </div>
                  <span className="text-lg font-bold">Fundo</span>
                </div>

                <button
                  className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800"
                  onClick={() => setMenuOpen(false)}
                >
                  <XIcon size={22} />
                </button>
              </div>

              {user &&
                navLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}

              {/* Theme Toggle */}
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="mt-6 w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                Toggle Theme
              </button>

              <div className="mt-8 text-xs opacity-70">
                © {new Date().getFullYear()} Fundo — Empowering African Agriculture.
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* MAIN */}
      <main className="max-w-7xl mx-auto px-4 py-6">{children}</main>

      {/* FOOTER */}
      <footer className="border-t border-gray-200 dark:border-gray-700 py-8 mt-12 text-center text-sm opacity-80">
        © {new Date().getFullYear()} Fundo — Powered by Ashita
      </footer>
    </div>
  );
};
